import type { QcHeatmapRange } from '../ColorRamp';
import type { ScanReviewRecord, ScanReviewRecordsFactory } from './ScanReviewRecordTypes';
import { ScanReviewDisplayType, ScanReviewPanelType } from './ScanReviewViewTypes';
import * as THREE from 'three';

export interface ScanReviewScene {
    setUpperJawVisibility(visible: boolean): void;
    setLowerJawVisibility(visible: boolean): void;
    setDisplayType(displayType: ScanReviewDisplayType, heatmapRange: QcHeatmapRange | null): void;
    setStoneModelDisplay(): void;
    setScanDisplay(): void;
    setHeatMapDisplay(): void;
    updateHeatMapRange(min: number, max: number): void;
}
export class ScanReviewPartialScene implements ScanReviewScene {
    scene: THREE.Scene;

    constructor(
        public upperJaw: ScanReviewRecord | null,
        public lowerJaw: ScanReviewRecord | null,
    ) {
        this.scene = new THREE.Scene();
        if (upperJaw) {
            this.scene.add(upperJaw.scanMesh);
        }
        if (lowerJaw) {
            this.scene.add(lowerJaw.scanMesh);
        }
    }

    setDisplayType(displayType: ScanReviewDisplayType, heatmapRange: QcHeatmapRange | null): void {
        if (displayType === ScanReviewDisplayType.Scan) {
            this.setScanDisplay();
        } else if (displayType === ScanReviewDisplayType.StoneModel) {
            this.setStoneModelDisplay();
        } else if (displayType === ScanReviewDisplayType.BiteAnalysis) {
            this.setHeatMapDisplay();
            if (heatmapRange) {
                this.updateHeatMapRange(heatmapRange.min, heatmapRange.max);
            }
        }
    }

    setUpperJawVisibility(visible: boolean) {
        this.upperJaw?.setVisible(visible);
    }

    setLowerJawVisibility(visible: boolean) {
        this.lowerJaw?.setVisible(visible);
    }

    setStoneModelDisplay() {
        this.upperJaw?.setStoneModelDisplay();
        this.lowerJaw?.setStoneModelDisplay();
    }

    setScanDisplay(): void {
        this.upperJaw?.setScanDisplay();
        this.lowerJaw?.setScanDisplay();
    }

    setHeatMapDisplay() {
        this.upperJaw?.setHeatMapDisplay();
        this.lowerJaw?.setHeatMapDisplay();
    }

    updateHeatMapRange(min: number, max: number) {
        this.upperJaw?.updateHeatMapRange?.(min, max);
        this.lowerJaw?.updateHeatMapRange?.(min, max);
    }
}

export class ScanReviewCompositeScene implements ScanReviewScene {
    private readonly upperView: ScanReviewPartialScene;
    private readonly lowerView: ScanReviewPartialScene;
    private readonly leftView: ScanReviewPartialScene;
    private readonly rightView: ScanReviewPartialScene;
    private readonly frontView: ScanReviewPartialScene;

    constructor(recordsFactory: ScanReviewRecordsFactory) {
        this.upperView = new ScanReviewPartialScene(recordsFactory()?.upperJaw, null);
        this.lowerView = new ScanReviewPartialScene(null, recordsFactory()?.lowerJaw);
        this.leftView = new ScanReviewPartialScene(recordsFactory().upperJaw, recordsFactory()?.lowerJaw);
        this.rightView = new ScanReviewPartialScene(recordsFactory().upperJaw, recordsFactory()?.lowerJaw);
        this.frontView = new ScanReviewPartialScene(recordsFactory().upperJaw, recordsFactory()?.lowerJaw);
    }

    private *partialScenes() {
        for (const partialScene of [this.upperView, this.lowerView, this.leftView, this.rightView, this.frontView]) {
            yield partialScene;
        }
    }

    getPartialSceneForPanelType(panelType: ScanReviewPanelType): ScanReviewPartialScene {
        switch (panelType) {
            case ScanReviewPanelType.Upper: {
                return this.upperView;
            }
            case ScanReviewPanelType.Lower: {
                return this.lowerView;
            }
            case ScanReviewPanelType.Left: {
                return this.leftView;
            }
            case ScanReviewPanelType.Right: {
                return this.rightView;
            }
            case ScanReviewPanelType.Front: {
                return this.frontView;
            }
        }
    }

    setUpperJawVisibility(visible: boolean): void {
        for (const partialScene of this.partialScenes()) {
            partialScene.setUpperJawVisibility(visible);
        }
    }
    setLowerJawVisibility(visible: boolean): void {
        for (const partialScene of this.partialScenes()) {
            partialScene.setLowerJawVisibility(visible);
        }
    }

    setDisplayType(displayType: ScanReviewDisplayType, heatmapRange: QcHeatmapRange | null): void {
        for (const partialScene of this.partialScenes()) {
            partialScene.setDisplayType(displayType, heatmapRange);
        }
    }

    setStoneModelDisplay() {
        for (const partialScene of this.partialScenes()) {
            partialScene.setStoneModelDisplay();
        }
    }
    setScanDisplay() {
        for (const partialScene of this.partialScenes()) {
            partialScene.setScanDisplay();
        }
    }
    setHeatMapDisplay() {
        for (const partialScene of this.partialScenes()) {
            partialScene.setHeatMapDisplay();
        }
    }
    updateHeatMapRange(min: number, max: number): void {
        for (const partialScene of this.partialScenes()) {
            partialScene.updateHeatMapRange(min, max);
        }
    }
}
